import { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { AppSidebar } from '@/components/sidebar';
import { BlobBackground } from '@/pages/onboarding/BlobBackground';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageTransition } from '@/components/layout/PageTransition';
import { ContentSkeleton } from '@/components/layout/ContentSkeleton';
import { PageChromeProvider, useShellPanelSlot } from '@/components/layout/PageChrome';
import { useNavTree } from '@/components/sidebar/hooks/useNavTree';
import { isScrollableRoute } from '@/constants/scrollableRoutes';

/**
 * Persistent shell for every protected page. The sidebar and header mount here
 * once and survive navigation; only the Outlet swaps. The Suspense boundary is
 * inside the shell so a lazy page chunk shows a content skeleton rather than
 * blanking the sidebar and header.
 *
 * Geometry is the Home Shell design's: a white ground carries the white rail
 * on the left, and the routed content sits on a grey sheet inset 9px from the
 * top with a hairline top/left border and a rounded top-left corner — the
 * sheet, not a sidebar border, is what separates the two. On the home lobby
 * the ambient blob wash paints inside the sheet, clipped by its corner.
 *
 * The nav tree is built once here and handed to both the sidebar and the
 * breadcrumb header, so the two can never disagree and the tree is not
 * recomputed per surface.
 */
export function AppShell() {
  return (
    <PageChromeProvider>
      <ShellFrame />
    </PageChromeProvider>
  );
}

/** The shell's markup, inside the provider so it can publish the panel slot. */
function ShellFrame() {
  const { pathname } = useLocation();
  const setPanelSlot = useShellPanelSlot();
  const sections = useNavTree();
  const overflowClass = isScrollableRoute(pathname) ? 'overflow-y-auto' : 'overflow-hidden';
  /* Matched by the router rather than compared as a string, so the shell can
     never disagree with the route tree about what "the lobby" is — an exact
     `pathname === '/ui'` missed the `/ui/` spelling and dropped the wash. */
  const isHomeLobby = Boolean(useMatch('/ui'));

  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);

  // On navigation, hand focus to the content region and reset its scroll, so a
  // keyboard user lands in the new page rather than on the link they clicked and
  // the page doesn't open half-scrolled. Skipped on first paint, and never steals
  // focus a page has already placed inside itself (the chat composer autofocuses).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const main = mainRef.current;
    if (!main) return;
    main.scrollTop = 0;
    if (!main.contains(document.activeElement)) {
      main.focus({ preventScroll: true });
    }
  }, [pathname]);

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-v2-md focus:bg-v2-background focus:px-3 focus:py-2 focus:text-v2-sm focus:text-v2-foreground focus:shadow-md focus:outline-none focus:ring-2 focus:ring-v2-ring"
      >
        Skip to content
      </a>
      <div className="flex h-screen bg-v2-background">
        <AppSidebar sections={sections} />
        {/* The secondary panel's slot. `display: contents` so a page's panel
            becomes a flex child of the shell row itself and the slot costs
            nothing on the routes that publish none. */}
        <div ref={setPanelSlot} className="contents" />
        {/* The content sheet. `isolate` + `overflow-hidden` clip the blob wash
            and page content to the rounded corner; the hairline is the
            design's rgba(0,0,0,.045) on a #F7F7F7 sheet.

            It carries the shell's surface shadow (`shadow-v2-surface`), the
            same one as the secondary panel beside it, so the island reads as
            a surface lifted off the page rather than a border drawn on it.
            Inset on every side and rounded all round, so a secondary panel can
            sit beside it as an equal surface rather than under its edge. It
            stacks above the panel on purpose: the panel runs 10px wider than it
            looks and hides that strip behind this sheet, which is what carries
            the hairline across the corner where the two meet. */}
        <div className="relative isolate z-[42] my-[9px] me-[9px] flex min-w-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-black/[0.045] bg-v2-sheet shadow-v2-surface dark:border-white/[0.06]">
          {/* `still`: the homepage design's wash holds one pose; the onboarding
              drift under a page this full pulls the eye off the content. */}
          {isHomeLobby && <BlobBackground position="absolute" still />}
          <PageHeader sections={sections} />
          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            className={`relative flex min-h-0 flex-1 flex-col outline-none ${overflowClass}`}
          >
            <PageTransition>
              <Suspense fallback={<ContentSkeleton />}>
                <Outlet />
              </Suspense>
            </PageTransition>
          </main>
        </div>
      </div>
    </>
  );
}
